import { useRouteError, useNavigate } from 'react-router-dom';
import { LuTriangleAlert, LuRefreshCw, LuHouse } from 'react-icons/lu';
import { Button } from '@/components/ui/button';
import { MainLayout } from 'src/pages/MainLayout';
import { SentryMonitoringService } from '@libs/frontend-services';
import { VITE_ENV } from 'src/config';
import { useEffect } from 'react';

const isLocal = VITE_ENV === 'local';

interface RouteError extends Error {
  status?: number;
  statusText?: string;
  data?: string;
}

export const GenericErrorFallback = () => {
  const error = useRouteError() as RouteError;
  const navigate = useNavigate();

  useEffect(() => {
    // Log error to Sentry
    const sentryService = new SentryMonitoringService();
    const errorId = Math.random().toString(36).substring(7);

    sentryService.captureException(error, {
      tags: {
        errorBoundaryLevel: 'route',
        errorId,
        errorStatus: error.status,
      },
      contexts: {
        errorBoundary: {
          errorId,
          timestamp: new Date().toISOString(),
          status: error.status,
          statusText: error.statusText,
        },
      },
      extra: {
        errorData: error.data,
      },
    });

    // Log to console in development
    if (isLocal) {
      // eslint-disable-next-line no-console
      console.error('Route error:', error);
    }
  }, [error]);

  const isInfiniteLoopError = error?.message?.includes('Maximum update depth exceeded');
  const isMoneyError =
    error?.message?.includes('You must provide an integer') ||
    error?.stack?.includes('dinero') ||
    error?.stack?.includes('MoneyFromCents') ||
    error?.stack?.includes('MoneyFromDollars');

  const getErrorTitle = () => {
    if (isInfiniteLoopError) return 'Render Loop Detected';
    if (isMoneyError) return 'Financial Calculation Error';
    if (error.status === 404) return 'Page Not Found';
    return 'Something went wrong';
  };

  const getErrorDescription = () => {
    if (isInfiniteLoopError) {
      return 'This page encountered a rendering issue and has been temporarily disabled. Our team has been notified.';
    }
    if (isMoneyError) {
      return 'We encountered an issue with financial calculations. This has been logged and our team will investigate.';
    }
    if (error.status === 404) {
      return "The page you're looking for doesn't exist or has been moved.";
    }
    return 'An unexpected error occurred on this page. You can try refreshing or navigate to another page.';
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <MainLayout>
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
        <div className="flex flex-col items-center text-center max-w-md">
          <LuTriangleAlert className="w-16 h-16 text-orange-500 mb-4" />

          <h2 className="text-2xl font-bold text-gray-900 mb-2">{getErrorTitle()}</h2>

          <p className="text-gray-600 mb-6">{getErrorDescription()}</p>

          {isLocal && error && (
            <details className="mb-6 p-4 bg-gray-100 rounded-md text-left text-sm max-w-full overflow-auto">
              <summary className="cursor-pointer font-medium text-red-600 mb-2">
                Error Details (Development)
              </summary>
              <pre className="whitespace-pre-wrap text-red-700">
                {error.message}
                {error.stack && `\n\nStack trace:\n${error.stack}`}
              </pre>
            </details>
          )}

          <div className="flex gap-3">
            <Button onClick={handleRefresh} className="flex items-center gap-2">
              <LuRefreshCw className="w-4 h-4" />
              Refresh Page
            </Button>
            <Button onClick={handleGoHome} variant="outline" className="flex items-center gap-2">
              <LuHouse className="w-4 h-4" />
              Go Home
            </Button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
